import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { splitEventTitle, formatEventDateRange } from '../components/EventTitle.jsx'
import CLUB from '../config/club.js'
import { FOURBALL_ONLY, PBCC_PREMIUM, IS_TEST_SITE } from '../config/siteMode.js'
import { resetToRegistration, resetToQualify, resetToTournament } from '../lib/stageReset.js'
import { setDemoFieldSize } from '../lib/demoField.js'
import { confirmAccessCodeIfLocked } from './PinGate.jsx'
import { ref, update } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import { Modal, Field } from './AdminApp.jsx'
import { useSetAdminNav } from './AdminShell.jsx'
import {
  addPlayer,
  autoPairFlight,
  closeTournamentWithResults,
  createFlight,
  createTeam,
  deleteFlight,
  deleteTeam,
  deleteTournament,
  generateBracket,
  lookupTeamTeeTimes,
  removePlayer,
  reopenTournament,
  setContest,
  setHoleScore,
  setTeamOpponent,
  updateTournamentInfo,
  lockTeamScores,
  watchClubCourses,
  watchClubPlayers,
} from '../lib/data.js'
import { autoAssignFlights, fmtVsPar, parsFor, rankTeams, teamGross, teamThru, teamVsPar } from '../lib/scoring.js'
import { lastNames } from '../lib/teamName.js'
import { autoFlightRegistrations, autoPairAllFlights, setFlightsLocked, setScrambleSettings, setTeamMulligansUsed, setTeamDrivesUsed, setTeamPlayerHandicaps, snapshotNetHandicaps } from '../lib/data.js'
import { saveTemplate } from '../lib/templates.js'
import {
  HANDICAP_MODE_OPTIONS, ALLOWANCE_OPTIONS, DEFAULT_ALLOWANCE,
  calcCourseHandicap, calcPlayingHandicap, normalizeGhin, isValidGhin,
} from '../lib/handicap.js'
import CourseSearchPicker from '../components/CourseSearchPicker.jsx'
import { lookupGhinHandicap } from '../lib/ghinLookup.js'
import { resolveAllowSelfReported } from '../lib/handicapEntry.js'
import QRPanel from './QRPanel.jsx'
import RegistrationTab from './RegistrationTab.jsx'
import QualifyingTab from './QualifyingTab.jsx'
import TeeTimesTab from './TeeTimesTab.jsx'
import BracketTab, { BracketAutoAdvance } from './BracketTab.jsx'
import CalcuttaTab from './CalcuttaTab.jsx'
import PhotosTab from './PhotosTab.jsx'
import { TvMonitorWall } from '../leaderboard/TvHub.jsx'
import LeaderboardApp from '../leaderboard/LeaderboardApp.jsx'
import HandicapReviewTab from './HandicapReviewTab.jsx'
import GCartSigns from './gg/GCartSigns.jsx'
import MessagesTab from './MessagesTab.jsx'
import ReportsTab from './ReportsTab.jsx'
import { IS_GOLF_ELITE } from '../config/siteMode.js'
import { DEFAULT_MOD_STABLEFORD } from '../lib/scoreFormats.js'
import HelpWizard from './HelpWizard.jsx'
import TeamCupTab from './TeamCupTab.jsx'

// Registration + Qualifying live on the main admin page (qualifying events →
// import as registrations); Players/handicap is managed there too — so those
// three are off the per-tournament tab bar.
const ALL_TABS = ['Overview', '📺 TV Monitor', '📺 TV Screen Control', 'Flights & Teams', 'Team Cup', 'Tee Times', 'Cart Signs', 'QR Codes', 'Live Scores', 'Live Leaderboard', 'Handicaps', 'Bracket', 'Contests', 'Photos', 'Calcutta', 'Messages', 'Reports', 'Results']

// Scramble has no bracket — it's stroke play with a leaderboard, period.
function visibleTabsFor(tournament) {
  const isScramble = tournament?.info?.format === 'Scramble'
  const isTeamCup = tournament?.info?.format === 'Team Cup'
  const isClosed = tournament?.info?.status === 'closed'
  // Tabs that don't apply to a Scramble: there's no qualifying round, no
  // individual-player handicap entry, no bracket, no Calcutta auction.
  // Scrambles run as a single field with one team per shotgun hole.
  const SCRAMBLE_HIDE = new Set(['Registration', 'Qualifying', 'Players', 'Bracket', 'Calcutta'])
  // Team Cup keeps Players + Tee Times but hides the bracket/calcutta
  // machinery and shows its own consolidated tab.
  const TEAMCUP_HIDE = new Set(['Bracket', 'Calcutta'])
  return ALL_TABS.filter((t) => {
    // 4-Ball-only site removes the Team Cup tab and the (retail) Cart Signs tab.
    if (FOURBALL_ONLY && (t === 'Team Cup' || t === 'Cart Signs')) return false
    // Messages (email/SMS comms) + live Reports are Golf Elite only.
    if ((t === 'Messages' || t === 'Reports') && !IS_GOLF_ELITE) return false
    if (isScramble && SCRAMBLE_HIDE.has(t)) return false
    if (isTeamCup && TEAMCUP_HIDE.has(t)) return false
    // The Team Cup tab itself only shows when the tournament IS Team Cup.
    if (t === 'Team Cup' && !isTeamCup) return false
    // TV Monitor + TV Screen Control are irrelevant once the round is over.
    if (isClosed && (t === '📺 TV Monitor' || t === '📺 TV Screen Control')) return false
    // Handicap review only applies when the event actually uses handicaps.
    if (t === 'Handicaps' && (tournament?.info?.handicapMode || 'none') === 'none') return false
    return true
  })
}

// Runs the bracket auto-advance for every bracketed flight no matter which tab
// is open, so a completed match advances the bracket in real time even when the
// admin isn't sitting on the Bracket tab. Each per-flight watcher is idempotent
// (only writes a winner / next-round slot when it's empty), so this running
// alongside the Bracket tab's own watcher is harmless.
function BracketAdvanceWatcher({ tournamentId, flights, brackets, pars }) {
  return (
    <>
      {flights
        .filter((f) => brackets?.[f.id]?.rounds)
        .map((f) => (
          <BracketAutoAdvance
            key={f.id}
            tournamentId={tournamentId}
            flight={f}
            bracket={brackets[f.id]}
            pars={pars}
            onNotice={() => {}}
          />
        ))}
    </>
  )
}

// Generates a bracket for every flight that doesn't have one yet, the moment
// the tournament is open — no matter which tab. This is why the TV Monitor and
// Full Screen show the WHOLE field right away instead of only the championship
// (which was the only bracket the template carried); previously the missing
// brackets were built only when the Bracket tab happened to be visited.
// `enabled` keeps it off for non-bracket formats (Scramble / Team Cup). One
// pass at a time via the ref guard; idempotent (only fills missing flights).
function BracketAutoGenerate({ tournamentId, flights, brackets, bracketDays, enabled }) {
  const busy = useRef(false)
  const flightIdsKey = flights.map((f) => f.id).join(',')
  const bracketIdsKey = Object.keys(brackets || {}).sort().join(',')
  useEffect(() => {
    if (!enabled || busy.current) return
    const todo = flights.filter((f) => !brackets?.[f.id])
    if (todo.length === 0) return
    busy.current = true
    ;(async () => {
      try {
        for (const f of todo) await generateBracket(tournamentId, f.id, bracketDays)
      } finally {
        busy.current = false
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightIdsKey, bracketIdsKey, tournamentId, bracketDays, enabled])
  return null
}

export default function TournamentDetail({ tournamentId, tournament }) {
  // Optional deep-link: /admin/t/:id?tab=Bracket opens straight on that tab
  // (used by the sidebar's Flights & Brackets entry). Falls back to Overview,
  // and the effect below still bounces to Overview if the tab is hidden for
  // this tournament's format.
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [tab, setTab] = useState(() => {
    const t = searchParams.get('tab')
    return t && ALL_TABS.includes(t) ? t : 'Overview'
  })
  const [showHelp, setShowHelp] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [showFieldSize, setShowFieldSize] = useState(false)
  // TEST-SITE-ONLY stage resets. Roll the demo back to a stage, keeping the full
  // roster, so it can be run forward again. Hard-gated to IS_TEST_SITE here AND
  // inside stageReset.js — never present or functional on pbccgolf.com.
  const runStageReset = async (label, fn) => {
    if (!IS_TEST_SITE || resetting) return
    if (!window.confirm(`Reset back to ${label}? This clears everything after it — continue?`)) return
    setResetting(true)
    try {
      const n = await fn(tournamentId)
      setTab('Overview')
      window.alert(`Reset to ${label}. Roster kept: ${n} teams.`)
    } catch (e) {
      window.alert(`Reset failed: ${e?.message || e}`)
    } finally {
      setResetting(false)
    }
  }
  const players = entriesAsList(tournament.players)
  const flights = entriesAsList(tournament.flights)
  // TEST demo field-size: master list size (once captured) governs the options;
  // before the first use it's the current full roster in flights.
  const masterCount = Object.keys(tournament.demoMaster?.teams || {}).length
  const flightTeamCount = flights.reduce((n, f) => n + Object.keys(f.teams || {}).length, 0)
  const maxFieldSize = Math.floor((masterCount || flightTeamCount || 104) / 8) * 8
  const curFieldSize = Number(tournament.info?.demoFieldSize) || flightTeamCount || maxFieldSize
  const pars = parsFor(tournament)
  const tabs = visibleTabsFor(tournament)
  // If the active tab gets hidden (e.g. tournament format flipped to
  // Scramble while the admin was on Bracket), bounce them to Overview.
  useEffect(() => {
    if (!tabs.includes(tab)) setTab('Overview')
  }, [tab, tabs])

  // Publish the tournament's tab menu to the shared AdminShell sidebar (this is
  // the "inside a tournament" nav). The shell renders the full-height sidebar +
  // zoom; this page fills the content area. deps include tab so the active
  // highlight updates.
  useSetAdminNav(PBCC_PREMIUM ? {
    back: { to: '/admin/pbcc-4ball', label: '← All tournaments' },
    items: [
      ...visibleTabsFor(tournament).map((t) => ({
        key: t,
        label: t,
        active: tab === t,
        onClick: () => { if (t === '📺 TV Screen Control') { navigate(`/tvcontrol?t=${tournamentId}`); return } setTab(t) },
      })),
      { key: '__help', label: '❓ Help', onClick: () => setShowHelp(true) },
      // TEST SITE ONLY — stage resets. Hidden entirely on pbccgolf.com.
      ...(IS_TEST_SITE ? [
        { key: '__reset_hdr', label: '— TEST: Demo —', disabled: true, onClick: () => {} },
        { key: '__field_size', label: `🧪 Field size: ${curFieldSize} teams`, onClick: () => setShowFieldSize(true) },
        { key: '__reset_reg', label: resetting ? '⏳ Resetting…' : '🧪 Reset → Registration', onClick: () => runStageReset('Registration', resetToRegistration) },
        { key: '__reset_qual', label: resetting ? '⏳ Resetting…' : '🧪 Reset → Qualify', onClick: () => runStageReset('Qualify', resetToQualify) },
        { key: '__reset_tourn', label: resetting ? '⏳ Resetting…' : '🧪 Reset → Tournament', onClick: () => runStageReset('Tournament', resetToTournament) },
      ] : []),
    ],
  } : null, [tab, tournament, resetting, curFieldSize])

  const watchers = (
    <>
      <BracketAdvanceWatcher tournamentId={tournamentId} flights={flights} brackets={tournament.brackets || {}} pars={pars} />
      <BracketAutoGenerate
        tournamentId={tournamentId}
        flights={flights}
        brackets={tournament.brackets || {}}
        bracketDays={Number(tournament.info?.bracketDays) || Number(tournament.info?.tournamentDays) || 1}
        enabled={tabs.includes('Bracket')}
      />
    </>
  )

  const practiceBanner = tournament.info?.practice ? (
    <div
      className="text-center font-extrabold uppercase tracking-widest text-xs px-4 py-2"
      style={{
        backgroundColor: 'rgba(var(--ge-primary-rgb), 0.18)',
        color: PBCC_PREMIUM ? 'var(--ge-cream-premium)' : 'var(--ge-primary)',
        borderBottom: `1.5px solid ${PBCC_PREMIUM ? 'var(--ge-gold)' : 'var(--ge-primary)'}`,
      }}>
      🎓 Practice Mode — sample data only · edit, score, and reset freely · nothing here affects real tournaments
    </div>
  ) : null

  const eventTitleBlock = FOURBALL_ONLY ? (
    /* 4-Ball site: fixed event branding (NOT the tournament's own name). */
    <div className="text-white" style={{ fontFamily: 'Cinzel, serif', textShadow: '0 2px 6px rgba(0,0,0,0.45)' }}>
      <div style={{ fontSize: 'clamp(13px, 1.5vw, 18px)', fontWeight: 600, letterSpacing: '0.06em', lineHeight: 1.4 }}>62nd Annual Pine Bluff Country Club</div>
      <div style={{ fontSize: 'clamp(18px, 2.4vw, 28px)', fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.2, marginTop: 4 }}>Four-Ball Match Play Invitational</div>
      <div style={{ fontSize: 'clamp(13px, 1.5vw, 18px)', fontWeight: 500, letterSpacing: '0.08em', lineHeight: 1.4, marginTop: 6 }}>July 30th – Aug 2nd, 2026</div>
    </div>
  ) : (
    <>
      <h1
        className="text-2xl sm:text-3xl"
        style={{ fontFamily: 'Cinzel, serif', fontWeight: 600, letterSpacing: '0.04em', color: '#FFFFFF', textShadow: '0 2px 6px rgba(0,0,0,0.45)', lineHeight: 1.25 }}>
        {(splitEventTitle(tournament.info).nameLines.length
          ? splitEventTitle(tournament.info).nameLines
          : [tournament.info?.name || 'Untitled']
        ).map((l, i) => <div key={i}>{l}</div>)}
      </h1>
      <p className="text-sm mt-1" style={{ color: '#FFFFFF' }}>
        {formatEventDateRange(tournament.info?.date, tournament.info?.endDate) || tournament.info?.date || 'Date TBD'}
      </p>
    </>
  )

  const tabNav = visibleTabsFor(tournament).map((t) => (
    <button
      key={t}
      onClick={() => {
        // TV Screen Control opens as its OWN full page (/tvcontrol). Carry the
        // tournament id so its sidebar tabs return to THIS tournament, not the
        // auto-resolved current event.
        if (t === '📺 TV Screen Control') { navigate(`/tvcontrol?t=${tournamentId}`); return }
        setTab(t)
      }}
      className={PBCC_PREMIUM
        ? `pbcc-sidebar-link ${tab === t ? 'pbcc-sidebar-link-active' : ''}`
        : `pbcc-tab whitespace-nowrap ${tab === t ? 'pbcc-tab-active' : ''}`}>
      {t}
    </button>
  ))

  const tabContent = (
    <>
      {tab === 'Overview' && <Overview tournamentId={tournamentId} tournament={tournament} info={tournament.info} flights={flights} players={players} />}
      {tab === 'Registration' && <RegistrationTab tournamentId={tournamentId} tournament={tournament} />}
      {tab === 'Qualifying' && <QualifyingTab tournamentId={tournamentId} tournament={tournament} />}
      {tab === 'Players' && <PlayersTab tournamentId={tournamentId} players={players} info={tournament.info} />}
      {tab === 'Flights & Teams' && <FlightsTab tournamentId={tournamentId} flights={flights} players={players} tournamentInfo={tournament.info} registrations={tournament.registrations} teeTimes={tournament.teeTimes || {}} tournament={tournament} />}
      {tab === 'Team Cup' && <TeamCupTab tournamentId={tournamentId} tournament={tournament} players={players} pars={pars} />}
      {tab === 'Tee Times' && <TeeTimesTab tournamentId={tournamentId} tournament={tournament} flights={flights} tournamentInfo={tournament.info} teeTimes={tournament.teeTimes || {}} brackets={tournament.brackets || {}} />}
      {tab === 'Cart Signs' && <GCartSigns tournament={tournament} />}
      {tab === 'Messages' && <MessagesTab tournamentId={tournamentId} tournament={tournament} />}
      {tab === 'Reports' && <ReportsTab tournament={tournament} />}
      {tab === 'Overview' && tournament.info?.format === 'Modified Stableford' && (
        <ModStablefordEditor tournamentId={tournamentId} info={tournament.info} />
      )}
      {tab === 'Live Scores' && <LiveScoresTab tournamentId={tournamentId} flights={flights} pars={pars} />}
      {tab === 'Live Leaderboard' && <LeaderboardApp tournamentIdProp={tournamentId} embedded />}
      {tab === 'Handicaps' && <HandicapReviewTab tournamentId={tournamentId} tournament={tournament} />}
      {tab === '📺 TV Monitor' && <TvMonitorWall tournamentId={tournamentId} />}
      {tab === 'Bracket' && <BracketTab tournamentId={tournamentId} tournament={tournament} flights={flights} brackets={tournament.brackets || {}} tournamentInfo={tournament.info} />}
      {tab === 'Contests' && <ContestsTab tournamentId={tournamentId} contests={tournament.contests || {}} />}
      {tab === 'Photos' && <PhotosTab tournamentId={tournamentId} tournament={tournament} />}
      {tab === 'QR Codes' && <QRPanel tournamentId={tournamentId} flights={flights} registrations={tournament.registrations || {}} tournamentInfo={tournament.info || {}} teeTimes={tournament.teeTimes || {}} photoAdmins={tournament.photoAdmins || {}} />}
      {tab === 'Calcutta' && <CalcuttaTab tournamentId={tournamentId} tournament={tournament} flights={flights} />}
      {tab === 'Results' && <ResultsTab tournamentId={tournamentId} tournament={tournament} info={tournament.info} flights={flights} pars={pars} />}
    </>
  )

  return (
    <>
      {watchers}
      {PBCC_PREMIUM ? (
        // Content only — the shared AdminShell provides the sidebar (tournament
        // tab menu, registered above) + zoom frame. .pbcc-tdetail scopes the
        // cream/gold card sweeps for the tabs.
        <div className="pbcc-tdetail flex-1 flex flex-col min-h-0">
          {practiceBanner}
          <div className="px-5 pt-6 pb-5 text-center">
            {eventTitleBlock}
          </div>
          <div className="pbcc-main-panel-4ball flex-1 min-h-0">
            <main className="p-4 sm:p-8">{tabContent}</main>
          </div>
        </div>
      ) : (
        <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
          {practiceBanner}
          <header style={{ backgroundColor: 'var(--ge-primary)', color: '#FFFFFF' }}>
            <div className="px-5 pt-4 pb-2 relative">
              <Link
                to="/admin/pbcc-4ball"
                className="absolute left-5 top-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest transition"
                style={{ color: 'rgba(255,255,255,0.85)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#FFFFFF' }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)' }}>
                ← All tournaments
              </Link>
              <button
                className="pbcc-btn-outline-gold absolute right-5 top-4"
                title={`Help for the ${tab} tab`}
                onClick={() => setShowHelp(true)}>
                ❓ Help
              </button>
              <div className="flex items-center pt-7 gap-4">
                <div className="flex-1 flex justify-center">
                  <img
                    src={CLUB.logoLight}
                    alt={CLUB.name}
                    className="h-24 sm:h-32 w-auto object-contain shrink-0"
                    style={{ filter: 'drop-shadow(0 3px 12px rgba(0,0,0,0.5))' }}
                  />
                </div>
                <div className="text-center">
                  {eventTitleBlock}
                </div>
                <div className="flex-1" aria-hidden="true" />
              </div>
            </div>
            <nav className={`px-3 sm:px-5 pt-1 flex gap-2 overflow-x-auto pb-3 ${(FOURBALL_ONLY || PBCC_PREMIUM) ? 'pbcc-tabs-4ball' : ''}`}>
              {tabNav}
            </nav>
          </header>
          <main className="max-w-6xl mx-auto p-4 sm:p-6">{tabContent}</main>
        </div>
      )}
      {showHelp ? (
        <HelpWizard contextKey={tab} onClose={() => setShowHelp(false)} />
      ) : null}
      {IS_TEST_SITE && showFieldSize ? (
        <DemoFieldSizeModal
          tournamentId={tournamentId}
          current={curFieldSize}
          maxSize={maxFieldSize}
          onClose={() => setShowFieldSize(false)}
        />
      ) : null}
    </>
  )
}

// TEST-ONLY: pick a demo field size (multiples of 8). Draws the top N teams by
// qualifying score from the master 104-team list and rebuilds a clean field.
function DemoFieldSizeModal({ tournamentId, current, maxSize, onClose }) {
  const options = []
  for (let s = 8; s <= maxSize; s += 8) options.push(s)
  const [size, setSize] = useState(options.includes(current) ? current : maxSize)
  const [busy, setBusy] = useState(false)
  const apply = async () => {
    if (busy) return
    if (!window.confirm(`Set the demo field to ${size} teams? Takes the top ${size} by qualifying score, re-splits into flights of 8, and clears all scores, brackets & auction data — the rest of the ${maxSize} stay benched in the master list.`)) return
    setBusy(true)
    try {
      const { size: applied, numFlights } = await setDemoFieldSize(tournamentId, size)
      window.alert(`Demo field set to ${applied} teams (${numFlights} flights of 8). Benched: ${maxSize - applied}.`)
      onClose()
    } catch (e) {
      window.alert(`Field size failed: ${e?.message || e}`)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal title="🧪 Demo Field Size (test only)" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-3">
        One master 104-team list is the source — nobody is deleted. Pick a size (multiples of 8): the
        <b> top N by qualifying score</b> re-split into flights of 8 as a clean, fresh field. Fully
        reversible — pick any size anytime.
      </p>
      <label className="block text-sm font-bold mb-1">Field size</label>
      <select
        value={size}
        onChange={(e) => setSize(Number(e.target.value))}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-bold">
        {options.map((s) => (
          <option key={s} value={s}>{s} teams ({s / 8} {s / 8 === 1 ? 'flight' : 'flights'}){s === maxSize ? ' — full field' : ''}</option>
        ))}
      </select>
      <p className="text-xs text-gray-500 mt-2">Currently: <b>{current} teams</b>.</p>
      <div className="flex gap-2 mt-5">
        <button onClick={onClose} className="flex-1 rounded-lg border border-gray-300 py-2.5 font-bold">Cancel</button>
        <button onClick={apply} disabled={busy} className="flex-1 rounded-lg py-2.5 font-extrabold text-white disabled:opacity-50" style={{ background: 'var(--ge-primary)' }}>
          {busy ? 'Applying…' : `Apply ${size}`}
        </button>
      </div>
    </Modal>
  )
}

function entriesAsList(obj) {
  return Object.entries(obj || {}).map(([id, v]) => ({ id, ...v }))
}

// ---------------------- Overview ----------------------

function Overview({ tournamentId, tournament, info, flights, players }) {
  const [editing, setEditing] = useState(false)
  // Ready-to-build banner: a 4-Ball tournament with a registered field but no
  // flights yet. This is the AI-build trigger on a real tournament (the old
  // one lived on the removed Qualifying tab). It fires the same window event
  // the AI Assistant listens for, so the floating ✦ circle opens + builds.
  const regCount = Object.keys(tournament.registrations || {}).length
  const is4Ball = (info?.format || '') === '4-Ball Best Ball'
  const canBuild = is4Ball && regCount > 0 && flights.length === 0
  const buildWithAI = () => {
    // autoSend so the AI just runs the build — no extra green Send click. That
    // manual step only belongs to the floating AI circle you open yourself.
    const command = `Build the PBCC 4-Ball tournament from the qualifying list with ${regCount} teams`
    try { window.dispatchEvent(new CustomEvent('ai-prefill', { detail: { command, open: true, autoSend: true } })) } catch {}
  }
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {canBuild ? (
        <div className="lg:col-span-3 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
             style={{ background: 'var(--ge-primary)', border: '1px solid var(--ge-primary)' }}>
          <div style={{ color: '#FFFFFF' }}>
            <div className="font-extrabold text-base" style={{ color: 'var(--ge-primary)' }}>Ready to build</div>
            <div className="text-sm mt-0.5">{regCount} team{regCount === 1 ? '' : 's'} registered — set up the flights, brackets, and tee times.</div>
          </div>
          <button
            type="button"
            onClick={buildWithAI}
            className="rounded-xl px-5 py-2.5 font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98]"
            style={{ background: 'var(--ge-primary)', color: 'var(--ge-primary)', border: '1px solid var(--ge-primary)' }}>
            ✦ Set Up with AI
          </button>
        </div>
      ) : null}
      <div className="pbcc-card-light p-5 lg:col-span-2">
        <div className="flex justify-between items-start">
          <div>
            <span className="pbcc-gold-rule" aria-hidden="true" />
            <h2 className="pbcc-h2 text-xl mt-1">Tournament Info</h2>
          </div>
          <button className="pbcc-btn-outline-gold" onClick={() => setEditing(true)}>Edit</button>
        </div>
        <dl className="grid grid-cols-2 gap-3 mt-4 text-sm" style={{ color: 'var(--ge-primary)' }}>
          <div><dt className="text-xs uppercase tracking-widest font-bold" style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>Name</dt><dd className="font-semibold mt-0.5">{info?.name}</dd></div>
          <div><dt className="text-xs uppercase tracking-widest font-bold" style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>Date</dt><dd className="font-semibold mt-0.5">{info?.date || '—'}</dd></div>
          <div><dt className="text-xs uppercase tracking-widest font-bold" style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>Course</dt><dd className="font-semibold mt-0.5">{info?.course || '—'}</dd></div>
          <div><dt className="text-xs uppercase tracking-widest font-bold" style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>Format</dt><dd className="font-semibold mt-0.5">{info?.format || '—'}</dd></div>
          <div><dt className="text-xs uppercase tracking-widest font-bold" style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>Status</dt><dd className="mt-0.5"><span className={info?.status === 'closed' ? 'pill-gray' : 'pill-green'}>{info?.status || 'open'}</span></dd></div>
          <div><dt className="text-xs uppercase tracking-widest font-bold" style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>Tee</dt><dd className="font-semibold mt-0.5">{info?.teeStyle || 'Tee times'}</dd></div>
        </dl>
        <div className="mt-5 flex gap-2 flex-wrap">
          <button
            className="pbcc-btn-green"
            onClick={async () => {
              if (info?.status === 'closed') {
                await reopenTournament(tournamentId)
              } else {
                if (!confirm('Close this tournament? Final results (1st/2nd/3rd per flight) will be locked into history.')) return
                await closeTournamentWithResults(tournamentId, tournament)
              }
            }}>
            {info?.status === 'closed' ? 'Re-open' : 'Close tournament'}
          </button>
          <button
            className="pbcc-btn-green"
            onClick={async () => {
              // Save the live tournament as a permanent /templates entry the
              // AI assistant can resolve forever — independent of /tournaments
              // and the demo TTL. Strips scores/results/aiLog/announcements/
              // tracking/photos/calcutta-bids on the way in.
              const defaultName = info?.name ? `${info.name} Template` : 'Tournament Template'
              const name = window.prompt('Save this tournament as a reusable template.\n\nGive it a name (e.g. "PBCC 4-Ball Championship"):', defaultName)
              if (!name || !name.trim()) return
              try {
                const res = await saveTemplate(name.trim(), tournament, { sourceTournamentId: tournamentId })
                window.alert(`Saved as template "${res.name}". The AI assistant can now use this whenever a 4-ball is requested.`)
              } catch (e) {
                window.alert(`Save failed: ${e?.message || String(e)}`)
              }
            }}>
            Save as Template
          </button>
          <button
            className="btn-secondary"
            onClick={() => updateTournamentInfo(tournamentId, { locked: !info?.locked })}
            title={info?.locked ? 'Locked — delete needs the access code' : 'Lock to guard against accidental delete'}>
            {info?.locked ? '🔒 Locked' : '🔓 Lock'}
          </button>
          <button
            className="btn-danger"
            onClick={() => {
              if (!confirmAccessCodeIfLocked(info?.locked)) return
              if (confirm('Delete this tournament and all its data? This cannot be undone.')) {
                deleteTournament(tournamentId)
                location.href = '/admin'
              }
            }}>
            Delete{info?.locked ? ' 🔒' : ''}
          </button>
        </div>
      </div>
      <div className="pbcc-card-light p-5">
        <span className="pbcc-gold-rule" aria-hidden="true" />
        <h2 className="pbcc-h2 text-xl mt-1">Snapshot</h2>
        <ul className="mt-4 space-y-2 text-sm" style={{ color: 'var(--ge-primary)' }}>
          <li className="flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-widest font-bold" style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>Players</span>
            <span className="font-extrabold text-lg" style={{ color: 'var(--ge-primary)' }}>{players.length}</span>
          </li>
          <li className="flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-widest font-bold" style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>Flights</span>
            <span className="font-extrabold text-lg" style={{ color: 'var(--ge-primary)' }}>{flights.length}</span>
          </li>
          <li className="flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-widest font-bold" style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>Teams</span>
            <span className="font-extrabold text-lg" style={{ color: 'var(--ge-primary)' }}>{flights.reduce((n, f) => n + Object.keys(f.teams || {}).length, 0)}</span>
          </li>
        </ul>
      </div>
      {info?.format === 'Scramble' ? (
        <ScrambleSettingsCard tournamentId={tournamentId} info={info} />
      ) : null}
      {info?.format === 'Stroke Play Individual' ? (
        <StrokePlayStartCard tournamentId={tournamentId} tournament={tournament} info={info} flights={flights} />
      ) : null}
      {editing ? <EditInfoModal tournamentId={tournamentId} info={info} onClose={() => setEditing(false)} /> : null}
    </div>
  )
}

// Live-editable scramble-format settings on the Overview tab. The card is
// collapsed by default — the typical scramble admin doesn't touch any of
// these toggles. Click "Advanced Options" to reveal handicaps / mulligans /
// minimum-drive rule. Each toggle writes immediately via setScrambleSettings,
// so phone scorecards and the TV leaderboard pick up changes through the
// existing tournament listener.
function ScrambleSettingsCard({ tournamentId, info }) {
  const teamSize = Number(info?.scrambleTeamSize) || 4
  const handicapsEnabled = !!info?.scrambleHandicapsEnabled
  const mulligans = Number(info?.scrambleMulligans) || 0
  const minDriveEnabled = !!info?.scrambleMinDriveEnabled
  const minDrivesPerPlayer = Number(info?.scrambleMinDrivesPerPlayer) || 0
  // Auto-expand when the admin already has at least one option turned on,
  // so they can see/edit existing values without an extra click.
  const anyOn = handicapsEnabled || mulligans > 0 || minDriveEnabled
  const [expanded, setExpanded] = useState(anyOn)

  const save = (partial) => {
    setScrambleSettings(tournamentId, partial).catch((e) => console.warn('[scramble] save failed', e))
  }

  const summary = anyOn
    ? [
        handicapsEnabled ? 'Handicaps' : null,
        mulligans > 0 ? `${mulligans} mulligan${mulligans === 1 ? '' : 's'}` : null,
        minDriveEnabled ? `Min ${minDrivesPerPlayer} drive${minDrivesPerPlayer === 1 ? '' : 's'}/player` : null,
      ].filter(Boolean).join(' · ')
    : 'All optional rules off (default)'

  return (
    <div className="card lg:col-span-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-left">
        <div className="min-w-0">
          <h2 className="text-lg font-bold">{teamSize}-Man Scramble · Advanced Options</h2>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{summary}</p>
        </div>
        <span className="text-gray-400 text-sm font-bold ml-3 shrink-0">
          {expanded ? '▾ Hide' : '▸ Show'}
        </span>
      </button>
      {!expanded ? null : (
      <>
      <p className="text-xs text-gray-500 mt-3 mb-3">
        Defaults are off — most scrambles don't use any of these. Phones and TV pick changes up live.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={handicapsEnabled}
            onChange={(e) => save({ handicapsEnabled: e.target.checked })}
          />
          <div className="flex-1">
            <div className="text-sm font-bold">Use Handicaps</div>
            <div className="text-xs text-gray-500">
              {teamSize === 2
                ? '2-man: 35% best + 15% partner. ON by default.'
                : '4-man scrambles usually skip handicaps. OFF by default.'}
            </div>
          </div>
        </label>

        <div>
          <div className="text-sm font-bold">Mulligans per team</div>
          <div className="flex items-center gap-2 mt-1">
            <select
              className="input flex-1"
              value={mulligans}
              onChange={(e) => save({ mulligans: Number(e.target.value) })}>
              {[0, 1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>{n === 0 ? 'None (default)' : `${n} per team`}</option>
              ))}
            </select>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Phone scorecards show a counter. Decrement on use.
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={minDriveEnabled}
            onChange={(e) => save({ minDriveEnabled: e.target.checked })}
          />
          <div className="flex-1">
            <div className="text-sm font-bold">Minimum Drive Rule</div>
            <div className="text-xs text-gray-500">
              OFF by default. Most clubs skip this. ON shows a per-player drive counter on the phone.
            </div>
          </div>
        </label>

        <div className={minDriveEnabled ? '' : 'opacity-50 pointer-events-none'}>
          <div className="text-sm font-bold">Minimum drives per player</div>
          <select
            className="input mt-1"
            disabled={!minDriveEnabled}
            value={minDrivesPerPlayer}
            onChange={(e) => save({ minDrivesPerPlayer: Number(e.target.value) })}>
            {[0, 1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>{n} drive{n === 1 ? '' : 's'}</option>
            ))}
          </select>
        </div>
      </div>
      </>
      )}
    </div>
  )
}

// Shotgun ↔ Tee Times toggle for Stroke Play Individual tournaments. Lives
// on the Overview tab alongside ScrambleSettingsCard. Default is Shotgun
// (matches the demo template). Switching to Tee Times generates sequential
// 8-minute interval tee times for each starting hole group (8:00 AM,
// 8:08, 8:16, …) by writing per-team assignments to
// teeTimes/1/waves/wave1/assignments. Switching back to Shotgun restores
// the hole-number assignments from the team.info.startingHole values.
function StrokePlayStartCard({ tournamentId, tournament, info, flights }) {
  const startType = info?.startType || 'shotgun'
  const isShotgun = startType === 'shotgun'

  async function setStart(nextStartType) {
    if (nextStartType === startType) return
    // Build the updated wave1 assignments map for every team in the
    // tournament. Group the teams by their startingHole (1..18). For
    // shotgun, each team's assignment is the hole number as a string.
    // For tee_times, each group gets a sequential time 8 minutes apart.
    const allTeams = []
    for (const f of flights || []) {
      for (const [tid, t] of Object.entries(f.teams || {})) {
        const startingHole = Number(t?.info?.startingHole) || 1
        allTeams.push({ tid, startingHole })
      }
    }
    const assignments = {}
    if (nextStartType === 'shotgun') {
      for (const { tid, startingHole } of allTeams) {
        assignments[tid] = String(startingHole)
      }
    } else {
      // Sequential — 8 minutes apart per group (each unique startingHole).
      // Group teams sharing a startingHole into the same 8-minute slot,
      // so a 2-player group both get 8:00 AM, the next group 8:08, etc.
      const uniqueHoles = Array.from(new Set(allTeams.map((x) => x.startingHole))).sort((a, b) => a - b)
      const slotByHole = new Map()
      uniqueHoles.forEach((hole, idx) => {
        const totalMinutes = 8 * 60 + idx * 8 // 8:00 AM + 8min × slot
        const h = Math.floor(totalMinutes / 60)
        const m = totalMinutes % 60
        const hh12 = ((h + 11) % 12) + 1
        const ampm = h < 12 ? 'AM' : 'PM'
        slotByHole.set(hole, `${hh12}:${String(m).padStart(2, '0')} ${ampm}`)
      })
      for (const { tid, startingHole } of allTeams) {
        assignments[tid] = slotByHole.get(startingHole) || '8:00 AM'
      }
    }
    const existingTeeTimes = tournament?.teeTimes && typeof tournament.teeTimes === 'object' ? tournament.teeTimes : {}
    const existingDay1 = existingTeeTimes[1] && typeof existingTeeTimes[1] === 'object' ? existingTeeTimes[1] : {}
    const existingWaves = existingDay1.waves && typeof existingDay1.waves === 'object' ? existingDay1.waves : {}
    const existingWave1 = existingWaves.wave1 && typeof existingWaves.wave1 === 'object' ? existingWaves.wave1 : {}
    await update(tref(`tournaments/${tournamentId}`), {
      'info/startType': nextStartType,
      'info/teeStyle': nextStartType === 'shotgun' ? 'Shotgun start' : 'Tee times',
      'teeTimes/1/waves/wave1': {
        ...existingWave1,
        startTime: existingWave1.startTime || '8:00 AM',
        flights: existingWave1.flights || (flights[0] ? { [flights[0].id]: true } : {}),
        assignments,
      },
    })
  }

  return (
    <div className="pbcc-card-light p-5 lg:col-span-3">
      <div className="flex justify-between items-start">
        <div>
          <span className="pbcc-gold-rule" aria-hidden="true" />
          <h2 className="pbcc-h2 text-xl mt-1">Stroke Play Start</h2>
          <p className="text-sm mt-1" style={{ color: 'rgba(var(--ge-primary-rgb),0.70)' }}>
            Shotgun puts every group on its assigned starting hole at the same time.
            Tee Times sends groups out from hole 1, 8 minutes apart.
          </p>
        </div>
      </div>
      <div className="mt-4 inline-flex rounded-lg overflow-hidden" style={{ border: '1.5px solid var(--ge-primary)' }}>
        <button
          type="button"
          onClick={() => setStart('shotgun')}
          className="px-4 py-2 text-sm font-extrabold transition"
          style={{
            background: isShotgun ? 'var(--ge-primary)' : '#FFFFFF',
            color: isShotgun ? 'var(--ge-primary)' : 'var(--ge-primary)',
          }}>
          🌀 Shotgun Start
        </button>
        <button
          type="button"
          onClick={() => setStart('tee_times')}
          className="px-4 py-2 text-sm font-extrabold transition"
          style={{
            background: !isShotgun ? 'var(--ge-primary)' : '#FFFFFF',
            color: !isShotgun ? 'var(--ge-primary)' : 'var(--ge-primary)',
            borderLeft: '1.5px solid var(--ge-primary)',
          }}>
          ⏱ Tee Times (8 min)
        </button>
      </div>
    </div>
  )
}

function EditInfoModal({ tournamentId, info, onClose }) {
  const [name, setName] = useState(info?.name || '')
  const [date, setDate] = useState(info?.date || '')
  const [course, setCourse] = useState(info?.course || '')
  const [format, setFormat] = useState(info?.format || '4-Ball Best Ball')
  const [playStyle, setPlayStyle] = useState(info?.playStyle || 'match')
  const [teeStyle, setTeeStyle] = useState(info?.teeStyle || 'Tee times')
  const [entryDeadline, setEntryDeadline] = useState(info?.entryDeadline || '')
  const [maxTeams, setMaxTeams] = useState(info?.maxTeams ?? '')
  const [registrationStatus, setRegistrationStatus] = useState(info?.registrationStatus || 'open')
  // Handicap settings — preserved from the create flow; lets the
  // director switch a tournament from scratch ↔ GHIN-required after
  // it's been opened. Default falls back to 'none' for older records.
  const [handicapMode, setHandicapMode] = useState(info?.handicapMode || 'none')
  // Course / tee selection. Hydrates from the tournament record so the
  // edit modal opens already showing the previously-picked course and
  // tee. teeBoxes is empty initially when reopening edit — the picker
  // would need a re-search to repopulate the tee dropdown, but the
  // already-selected tee details (slope/rating/par) remain valid.
  const [courseSel, setCourseSel] = useState({
    courseName: info?.courseName || '',
    courseId: info?.courseId || null,
    coursePar: info?.coursePar != null ? Number(info.coursePar) : null,
    slopeRating: info?.slopeRating != null ? Number(info.slopeRating) : null,
    courseRating: info?.courseRating != null ? Number(info.courseRating) : null,
    teeName: info?.teeName || null,
    teeBoxes: info?.teeName && info?.slopeRating && info?.courseRating
      ? [{
          name: info.teeName,
          slope: Number(info.slopeRating),
          rating: Number(info.courseRating),
          par: Number(info.coursePar) || 72,
        }]
      : [],
  })
  const [handicapAllowance, setHandicapAllowance] = useState(
    Number.isFinite(Number(info?.handicapAllowance)) ? Number(info.handicapAllowance) : DEFAULT_ALLOWANCE,
  )
  const [allowSelfReported, setAllowSelfReported] = useState(resolveAllowSelfReported(info))
  const submit = async (e) => {
    e.preventDefault()
    await updateTournamentInfo(tournamentId, {
      name, date, course, format, playStyle, teeStyle,
      entryDeadline: entryDeadline || null,
      maxTeams: maxTeams === '' ? null : Number(maxTeams),
      registrationStatus,
      handicapMode,
      slopeRating: handicapMode === 'none' ? null : (Number(courseSel.slopeRating) || null),
      courseRating: handicapMode === 'none' ? null : (Number(courseSel.courseRating) || null),
      coursePar: handicapMode === 'none' ? null : (Number(courseSel.coursePar) || null),
      courseName: handicapMode === 'none' ? null : (courseSel.courseName || null),
      courseId: handicapMode === 'none' ? null : (courseSel.courseId || null),
      teeName: handicapMode === 'none' ? null : (courseSel.teeName || null),
      handicapAllowance: handicapMode === 'none' ? null : Number(handicapAllowance),
      allowSelfReported: handicapMode === 'none' ? null : allowSelfReported,
    })
    onClose()
  }
  return (
    <Modal title="Edit Tournament" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Date"><input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Course"><input className="input" value={course} onChange={(e) => setCourse(e.target.value)} /></Field>
        <Field label="Format">
          <select className="input" value={format} onChange={(e) => setFormat(e.target.value)}>
            <option>4-Ball Best Ball</option>
            <option>Stroke Play</option>
            <option>Scramble</option>
            <option>Shamble</option>
            <option>Alternate Shot</option>
            <option>Stableford</option>
            <option>Team Cup</option>
          </select>
        </Field>
        <Field label="Scoring style">
          <select className="input" value={playStyle} onChange={(e) => setPlayStyle(e.target.value)}>
            <option value="match">Match Play</option>
            <option value="stroke">Stroke Play</option>
          </select>
        </Field>
        <Field label="Tee style">
          <select className="input" value={teeStyle} onChange={(e) => setTeeStyle(e.target.value)}>
            <option>Tee times</option>
            <option>Shotgun start</option>
          </select>
        </Field>
        <Field label="Entry deadline">
          <input type="date" className="input" value={entryDeadline} onChange={(e) => setEntryDeadline(e.target.value)} />
        </Field>
        <Field label="Max teams (optional)">
          <input type="number" min="1" className="input" value={maxTeams} onChange={(e) => setMaxTeams(e.target.value)} placeholder="Leave blank for no limit" />
        </Field>
        <Field label="Registration status">
          <select className="input" value={registrationStatus} onChange={(e) => setRegistrationStatus(e.target.value)}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="waitlist">Waitlist</option>
          </select>
        </Field>
        <Field label="Handicap Mode">
          <select
            className="input"
            value={handicapMode}
            onChange={(e) => setHandicapMode(e.target.value)}>
            {HANDICAP_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="text-xs text-gray-600 mt-1.5 leading-snug">
            {(HANDICAP_MODE_OPTIONS.find((o) => o.value === handicapMode) || {}).desc}
          </p>
        </Field>
        {handicapMode !== 'none' ? (
          <>
            <SavedCoursesQuickPick onPick={setCourseSel} />
            <CourseSearchPicker value={courseSel} onChange={setCourseSel} />
            <Field label="Handicap Allowance">
              <select
                className="input"
                value={handicapAllowance}
                onChange={(e) => setHandicapAllowance(Number(e.target.value))}>
                {ALLOWANCE_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}%</option>
                ))}
              </select>
              <p className="text-xs text-gray-600 mt-1.5 leading-snug">
                Applied to every player's course handicap. Default 100% = no
                adjustment. Lower allowance = fewer strokes = tighter field.
              </p>
            </Field>
            <Field label="Self-Reported Handicaps">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={allowSelfReported}
                  onChange={(e) => setAllowSelfReported(e.target.checked)} />
                <span className="text-sm">
                  Allow players to enter a handicap by hand
                  <span className="block text-xs text-gray-600 mt-0.5 leading-snug">
                    On = (GH)# lookup <b>or</b> a typed index (self-reported, flagged for review).
                    Off = <b>(GH)#-verified only</b> — the manual index field is hidden and
                    registration is blocked until every player verifies.
                  </span>
                </span>
              </label>
            </Field>
          </>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary">Save</button>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------- Players ----------------------

function PlayersTab({ tournamentId, players, info }) {
  const [showAdd, setShowAdd] = useState(false)
  // Handicap columns only appear when the director enabled handicap
  // mode for this tournament. Scratch events keep the original 4-column
  // layout unchanged.
  const showHcp = (info?.handicapMode || 'none') !== 'none'
  const slopeRating = Number(info?.slopeRating) || 113
  const courseRating = Number(info?.courseRating) || null
  const coursePar = Number(info?.coursePar) || null
  const allowance = Number.isFinite(Number(info?.handicapAllowance))
    ? Number(info.handicapAllowance) : DEFAULT_ALLOWANCE
  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold">Players ({players.length})</h2>
        <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add Player</button>
      </div>
      {players.length === 0 ? (
        <p className="text-gray-500 text-sm">No players yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2">Name</th>
                {showHcp ? <th className="text-left py-2">Handicap #</th> : null}
                {showHcp ? <th className="text-right py-2">Idx</th> : null}
                {showHcp ? <th className="text-right py-2">CH</th> : null}
                {showHcp ? <th className="text-right py-2">PH</th> : null}
                {!showHcp ? <th className="text-left py-2">Handicap</th> : null}
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Phone</th>
                <th className="text-right py-2"></th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => {
                // Prefer explicit per-player fields when they exist;
                // fall back to deriving CH / PH from handicapIndex (or
                // legacy `handicap`) using the tournament's slope and
                // allowance so older records still render numbers.
                const idx = p.handicapIndex != null ? Number(p.handicapIndex)
                  : (p.handicap != null ? Number(p.handicap) : null)
                const ch = p.courseHandicap != null ? Number(p.courseHandicap)
                  : (Number.isFinite(idx) ? calcCourseHandicap(idx, slopeRating, courseRating, coursePar) : null)
                const ph = p.playingHandicap != null ? Number(p.playingHandicap)
                  : (ch != null ? calcPlayingHandicap(ch, allowance) : null)
                return (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-2 font-semibold">{p.name}</td>
                    {showHcp ? <td>{p.ghin || '—'}</td> : null}
                    {showHcp ? <td className="text-right">{idx ?? '—'}</td> : null}
                    {showHcp ? <td className="text-right">{ch ?? '—'}</td> : null}
                    {showHcp ? <td className="text-right">{ph ?? '—'}</td> : null}
                    {!showHcp ? <td>{p.handicap ?? '—'}</td> : null}
                    <td>{p.email || '—'}</td>
                    <td>{p.phone || '—'}</td>
                    <td className="text-right">
                      <button
                        className="text-red-600 hover:text-red-800 text-xs font-semibold"
                        onClick={() => removePlayer(tournamentId, p.id)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {showAdd ? <AddPlayerModal tournamentId={tournamentId} info={info} onClose={() => setShowAdd(false)} /> : null}
    </div>
  )
}

function AddPlayerModal({ tournamentId, info, onClose }) {
  const handicapMode = info?.handicapMode || 'none'
  const showHcp = handicapMode !== 'none'
  const ghinRequired = handicapMode === 'required'
  const allowSelfReported = resolveAllowSelfReported(info)
  const slopeRating = Number(info?.slopeRating) || 113
  const courseRating = Number(info?.courseRating) || null
  const coursePar = Number(info?.coursePar) || null
  const allowance = Number.isFinite(Number(info?.handicapAllowance))
    ? Number(info.handicapAllowance) : DEFAULT_ALLOWANCE
  const [name, setName] = useState('')
  const [handicap, setHandicap] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  // Handicap-mode-only fields. Index doubles as the legacy `handicap`
  // value when saving so the rest of the app continues to read it.
  const [ghin, setGhin] = useState('')
  const [handicapIndex, setHandicapIndex] = useState('')
  // Live GHIN lookup status — drives the loading/notfound message and
  // hides the manual Index field on success.
  const [ghinStatus, setGhinStatus] = useState('idle')
  // Handicap SOURCE flag: 'ghin' once a lookup fills the index, else 'self'.
  const [source, setSource] = useState('self')
  const runGhinLookup = async () => {
    const clean = (ghin || '').replace(/\D/g, '')
    if (clean.length < 7) {
      setGhinStatus(clean.length === 0 ? 'idle' : 'notfound')
      return
    }
    setGhinStatus('loading')
    const res = await lookupGhinHandicap(clean)
    if (res.ok) {
      setHandicapIndex(String(res.handicapIndex))
      setSource('ghin')
      // Auto-fill the player's name from the GHIN record. Only
      // overwrites when GHIN actually returned one — preserves any
      // name the admin already typed if the API didn't carry one.
      if (res.fullName) setName(res.fullName)
      setGhinStatus('ok')
    } else {
      setGhinStatus(res.reason || 'notfound')
    }
  }
  const idxNum = handicapIndex === '' ? null : Number(handicapIndex)
  const ch = Number.isFinite(idxNum) && idxNum != null
    ? calcCourseHandicap(idxNum, slopeRating, courseRating, coursePar) : null
  const ph = ch != null ? calcPlayingHandicap(ch, allowance) : null
  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    // (GH)#-verified-only events: block a self-reported / missing index.
    if (showHcp && !allowSelfReported && (idxNum == null || source !== 'ghin')) {
      window.alert('This event requires a (GH)#-verified handicap. Enter a valid (GH)# so it can be verified.')
      return
    }
    const payload = {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
    }
    if (showHcp) {
      payload.ghin = normalizeGhin(ghin) || null
      payload.handicapIndex = idxNum
      payload.courseHandicap = ch
      payload.playingHandicap = ph
      // SOURCE flag ('ghin' | 'self') for the who's-self-reported review.
      payload.handicapSource = idxNum != null ? source : null
      payload.handicapVerifiedAt = (idxNum != null && source === 'ghin') ? Date.now() : null
      // Mirror to the legacy `handicap` field so existing readers
      // (Flights & Teams, Tee Times, scoring) keep working.
      payload.handicap = idxNum
    } else {
      payload.handicap = handicap === '' ? null : Number(handicap)
    }
    await addPlayer(tournamentId, payload)
    onClose()
  }
  // Pre-fill from a saved roster player. Always populates name + email
  // + GHIN; only fills handicapIndex when one was saved on the roster
  // (otherwise leaves the field for manual entry).
  const fromRoster = (p) => {
    setName(p.name || '')
    setEmail(p.email || '')
    setGhin(p.ghin || '')
    if (p.handicapIndex != null) setHandicapIndex(String(p.handicapIndex))
  }

  return (
    <Modal title="Add Player" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <SavedPlayersQuickPick onPick={fromRoster} />
        <Field label="Name"><input autoFocus className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        {showHcp ? (
          <>
            <Field label={`(GH)#${ghinRequired ? ' *' : ''}`}>
              <input
                className="input"
                inputMode="numeric"
                value={ghin}
                onChange={(e) => {
                  setGhin(e.target.value)
                  if (ghinStatus !== 'idle') setGhinStatus('idle')
                }}
                onBlur={runGhinLookup}
                placeholder="e.g. 1234567"
                maxLength={10} />
            </Field>
            <AdminGhinStatusLine status={ghinStatus} index={handicapIndex} />
            {ghinStatus !== 'ok' && allowSelfReported ? (
              <Field label="Handicap Index">
                <input
                  type="number"
                  step="0.1"
                  className="input"
                  value={handicapIndex}
                  onChange={(e) => { setHandicapIndex(e.target.value); setSource('self') }}
                  placeholder="e.g. 12.4" />
              </Field>
            ) : null}
            {ch != null ? (
              <div className="rounded-lg px-3 py-2 text-sm bg-white border border-gray-200 flex justify-between">
                <span className="uppercase tracking-widest text-gray-500 text-xs">
                  Course HCP / Playing HCP
                </span>
                <span className="font-extrabold">{ch} / {ph}</span>
              </div>
            ) : null}
          </>
        ) : (
          <Field label="Handicap"><input type="number" step="0.1" className="input" value={handicap} onChange={(e) => setHandicap(e.target.value)} /></Field>
        )}
        <Field label="Email"><input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Phone"><input type="tel" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary">Add</button>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------- Flights & Teams ----------------------

function FlightsTab({ tournamentId, flights, players, tournamentInfo, registrations, teeTimes, tournament }) {
  const [showCreate, setShowCreate] = useState(false)
  const [autoModal, setAutoModal] = useState(false)
  const [flightFromRegs, setFlightFromRegs] = useState(null) // 'qualifyingScore' | 'handicap' | 'random' | null
  const isShotgun = (tournamentInfo?.teeStyle || '') === 'Shotgun start'
  const startType = tournamentInfo?.startType || (isShotgun ? 'shotgun' : 'sequential')
  const locked = !!tournamentInfo?.flightsLocked
  const pars = parsFor(tournament || { info: tournamentInfo })
  const coursePar = pars.reduce((s, p) => s + (Number(p) || 0), 0) || 72

  const teamCount = flights.reduce((n, f) => n + Object.keys(f.teams || {}).length, 0)
  const unflightedRegs = Object.entries(registrations || {})
    .filter(([, r]) => !r.flighted)
    .map(([id, r]) => ({ id, ...r }))

  const toggleLock = async () => {
    await setFlightsLocked(tournamentId, !locked)
  }

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">
            Flights ({flights.length}) · Teams ({teamCount})
            {locked ? <span className="ml-2 pill-gray text-[10px]">🔒 Locked</span> : null}
          </h2>
          <p className="text-sm text-gray-500">
            Up to 20 flights, unlimited teams per flight.
            {unflightedRegs.length > 0 ? <> · <b>{unflightedRegs.length}</b> registered, not yet flighted.</> : null}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            className="btn-secondary"
            disabled={locked || unflightedRegs.length === 0}
            onClick={() => setFlightFromRegs('qualifyingScore')}
            title="Sort by qualifying score (lowest first) and split into N flights">
            ⛳ Auto-Flight by Qualifying
          </button>
          <button
            className="btn-secondary"
            disabled={locked || unflightedRegs.length === 0}
            onClick={() => setFlightFromRegs('handicap')}>
            ⛳ Auto-Flight by Handicap
          </button>
          <button
            className="btn-secondary"
            disabled={locked || unflightedRegs.length === 0}
            onClick={() => setFlightFromRegs('random')}
            title="Blind draw — randomly distribute teams across flights">
            🎲 Auto-Flight Random
          </button>
          <button
            className="btn-secondary"
            disabled={locked || flights.length === 0 || teamCount === 0}
            onClick={async () => {
              if (!confirm(`Auto-pair all ${flights.length} flights? Pairs teams in order: 1↔2, 3↔4, … in every flight.`)) return
              const n = await autoPairAllFlights(tournamentId)
              alert(`Paired ${n} flight${n === 1 ? '' : 's'}.`)
            }}
            title="Pair every flight at once — 1↔2, 3↔4, …">
            ⇄ Auto-Pair All Flights
          </button>
          <button
            className="btn-secondary"
            disabled={locked || flights.length === 0 || players.length === 0}
            onClick={() => setAutoModal(true)}>
            Pair players → teams
          </button>
          <button
            className="btn-primary"
            disabled={locked || flights.length >= 20}
            onClick={() => setShowCreate(true)}>
            + New Flight
          </button>
          <button
            className={`text-xs font-bold ${locked ? 'text-amber-700' : 'text-gray-700'}`}
            onClick={toggleLock}>
            {locked ? '🔓 Unlock Flights' : '🔒 Lock Flights'}
          </button>
        </div>
      </div>

      {flights.length === 0 ? (
        <div className="card text-center py-10 text-gray-500">No flights yet.</div>
      ) : (
        flights.map((f) => (
          <FlightCard
            key={f.id}
            tournamentId={tournamentId}
            flight={f}
            isShotgun={isShotgun}
            startType={startType}
            teeTimes={teeTimes}
            coursePar={coursePar}
            tournamentInfo={tournamentInfo}
          />
        ))
      )}

      {showCreate ? (
        <CreateFlightModal tournamentId={tournamentId} onClose={() => setShowCreate(false)} flightCount={flights.length} />
      ) : null}
      {autoModal ? (
        <AutoAssignModal
          tournamentId={tournamentId}
          flights={flights}
          players={players}
          onClose={() => setAutoModal(false)}
        />
      ) : null}
      {flightFromRegs ? (
        <AutoFlightModal
          tournamentId={tournamentId}
          unflightedRegs={unflightedRegs}
          sortBy={flightFromRegs}
          isPbcc4Ball={!!tournamentInfo?.pbcc4Ball}
          onClose={() => setFlightFromRegs(null)}
        />
      ) : null}
    </div>
  )
}

function AutoFlightModal({ tournamentId, unflightedRegs, sortBy, isPbcc4Ball, onClose }) {
  // Two control modes:
  //   PBCC 4-Ball — the admin picks the flight count directly (10..14,
  //                 default 12); teams-per-flight is derived.
  //   Everything else — the admin picks teams-per-flight (default 8);
  //                     flight count is derived and capped at 12 so the
  //                     naming stays coherent (Championship + Flight 1..11).
  const [teamsPerFlight, setTeamsPerFlight] = useState(8)
  const [pbccFlights, setPbccFlights] = useState(12)
  const [busy, setBusy] = useState(false)
  const sortLabel = sortBy === 'qualifyingScore' ? 'Qualifying Score (best first)'
    : sortBy === 'handicap' ? 'Handicap (lowest first)'
    : 'Random (blind draw)'

  const sorted = useMemo(() => {
    const r = unflightedRegs.slice()
    if (sortBy === 'random') return r.sort(() => Math.random() - 0.5)
    if (sortBy === 'qualifyingScore') return r.sort((a, b) => (Number(a.qualifyingScore) || Infinity) - (Number(b.qualifyingScore) || Infinity))
    return r.sort((a, b) => (Number(a.handicap) ?? 99) - (Number(b.handicap) ?? 99))
  }, [unflightedRegs, sortBy])

  let numFlights, perFlight
  if (isPbcc4Ball) {
    numFlights = Math.max(10, Math.min(14, Number(pbccFlights) || 12))
    perFlight = Math.max(1, Math.ceil(sorted.length / numFlights))
  } else {
    perFlight = Math.max(1, Number(teamsPerFlight) || 1)
    numFlights = Math.min(12, Math.max(1, Math.ceil(sorted.length / perFlight)))
  }
  const buckets = []
  for (let i = 0; i < numFlights; i++) buckets.push(sorted.slice(i * perFlight, (i + 1) * perFlight))

  const flightLabel = (i) => (i === 0 ? 'Championship Flight' : `Flight ${i}`)
  const labelFor = (i) => {
    if (sortBy === 'qualifyingScore') return i === 0 ? 'Best scores' : i === numFlights - 1 ? 'Highest scores' : ''
    if (sortBy === 'handicap')        return i === 0 ? 'Lowest handicaps' : i === numFlights - 1 ? 'Highest handicaps' : ''
    return ''
  }

  const apply = async () => {
    setBusy(true)
    try {
      await autoFlightRegistrations(
        tournamentId,
        Object.fromEntries(unflightedRegs.map((r) => [r.id, r])),
        { numFlights, sortBy },
      )
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Auto-Flight Preview" onClose={onClose}>
      <p className="text-sm text-gray-600">
        <b>{unflightedRegs.length}</b> teams ÷ <b>{perFlight}</b> per flight → <b>{numFlights}</b> flight{numFlights === 1 ? '' : 's'}
      </p>
      <p className="text-xs text-gray-500 mt-1">Sort by: {sortLabel}</p>
      {isPbcc4Ball ? (
        <div className="mt-3 flex items-center gap-2">
          <label className="text-sm font-bold">Number of flights:</label>
          <input
            type="number" min="10" max="14" className="w-20 input" value={pbccFlights}
            onChange={(e) => setPbccFlights(Math.max(10, Math.min(14, Number(e.target.value) || 12)))}
          />
          <span className="text-xs text-gray-500">PBCC 4-Ball: 10–14 flights (default 12)</span>
        </div>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-2">
            <label className="text-sm font-bold">Teams per flight:</label>
            <input
              type="number" min="1" max="50" className="w-20 input" value={teamsPerFlight}
              onChange={(e) => setTeamsPerFlight(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            />
          </div>
          {sorted.length / perFlight > 12 ? (
            <p className="text-xs text-amber-600 mt-1.5">
              Capped at 12 flights — increase teams per flight to fit more teams.
            </p>
          ) : null}
        </>
      )}

      <div className="mt-4 max-h-72 overflow-y-auto space-y-3">
        {buckets.map((teams, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-2">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold flex justify-between">
              <span>{flightLabel(i)}</span>
              {labelFor(i) ? <span className="text-gray-400 italic normal-case">{labelFor(i)}</span> : null}
            </div>
            <ol className="text-sm mt-1">
              {teams.map((t, k) => {
                const stat = sortBy === 'qualifyingScore' ? (t.qualifyingScore ?? '—')
                  : sortBy === 'handicap' ? (t.handicap ?? '—')
                  : ''
                return (
                  <li key={t.id} className="flex justify-between border-b last:border-0 py-0.5">
                    <span><b>{i * perFlight + k + 1}.</b> {t.teamName || (t.players || []).join(' / ')}</span>
                    {stat !== '' ? <span className="text-gray-500 text-xs">{stat}</span> : null}
                  </li>
                )
              })}
              {teams.length === 0 ? <li className="text-gray-400 italic text-xs">empty</li> : null}
            </ol>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-3">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={apply} disabled={busy}>
          {busy ? 'Assigning…' : 'Confirm & Assign'}
        </button>
      </div>
    </Modal>
  )
}

function FlightCard({ tournamentId, flight, isShotgun, startType, teeTimes, coursePar, tournamentInfo }) {
  // Per-player handicap inputs only render for Scramble events with the
  // handicaps toggle on. Saves to team.info.handicaps (parallel to
  // team.info.players); scoring.js's scrambleHandicapStrokes reads it.
  const showHcps =
    tournamentInfo?.format === 'Scramble' && !!tournamentInfo?.scrambleHandicapsEnabled
  // Scramble has no qualifying scores and no head-to-head pairings — both
  // are 4-Ball / match-play concepts. Hide those slots so admins don't see
  // (or accidentally fill in) data that doesn't apply to scrambles.
  const isScramble = tournamentInfo?.format === 'Scramble'
  const [showAddTeam, setShowAddTeam] = useState(false)
  const teams = entriesAsList(flight.teams)
  const teamNameById = Object.fromEntries(teams.map((t) => [t.id, t.info?.name]))

  const onAutoPair = async () => {
    if (teams.length < 2) return
    if (!confirm('Auto-pair this flight? Pairs teams in order: 1↔2, 3↔4, …')) return
    await autoPairFlight(tournamentId, flight.id)
  }

  const onSetOpponent = async (teamId, opponentId) => {
    await setTeamOpponent(tournamentId, flight.id, teamId, opponentId || null)
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h3 className="font-bold text-base">{flight.info?.name}</h3>
          <p className="text-xs text-gray-500">{flight.info?.format || '4-Ball Best Ball'}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            className="btn-secondary text-xs"
            disabled={teams.length < 2}
            onClick={onAutoPair}
            title="Pairs teams in order: 1↔2, 3↔4, 5↔6 …">
            ⇄ Auto-pair
          </button>
          <button
            className="btn-secondary text-xs"
            onClick={() => setShowAddTeam(true)}>
            + Add Team
          </button>
          <button
            className="text-red-600 text-xs font-semibold"
            onClick={() => {
              if (confirm(`Delete flight "${flight.info?.name}"?`)) deleteFlight(tournamentId, flight.id)
            }}>
            Delete
          </button>
        </div>
      </div>
      {teams.length === 0 ? (
        <p className="text-sm text-gray-500 mt-3">No teams in this flight yet.</p>
      ) : (
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {teams.map((t) => {
            const oppId = t.info?.opponentId
            const oppName = oppId ? teamNameById[oppId] : null
            return (
              <div key={t.id} className="rounded-xl border border-gray-200 px-3 py-2">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{lastNames(t.info?.name)}</div>
                    {showHcps ? (
                      <PlayerHandicapEditor
                        tournamentId={tournamentId}
                        flightId={flight.id}
                        team={t}
                      />
                    ) : (
                      <>
                        <div className="text-xs text-gray-500 truncate">{(t.info?.players || []).join(' · ')}</div>
                        {t.info?.handicap != null ? (
                          <div className="text-xs text-gray-400 mt-0.5">HCP {t.info.handicap}</div>
                        ) : null}
                      </>
                    )}
                  </div>
                  <button
                    className="text-red-600 text-xs ml-2"
                    onClick={() => deleteTeam(tournamentId, flight.id, t.id)}>×</button>
                </div>
                {isScramble ? null : (
                  <>
                    <QualifyingDisplay score={t.info?.qualifyingScore} coursePar={coursePar} />
                    <div className="mt-2 flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold shrink-0">vs</span>
                      <select
                        className="flex-1 text-xs rounded-md border border-gray-300 px-1.5 py-1 bg-white"
                        value={oppId || ''}
                        onChange={(e) => onSetOpponent(t.id, e.target.value)}>
                        <option value="">No opponent</option>
                        {teams.filter((x) => x.id !== t.id).map((x) => (
                          <option key={x.id} value={x.id}>{lastNames(x.info?.name)}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                {isShotgun ? (
                  <TeeTimeReadOnly
                    teeTimes={teeTimes}
                    startType={startType}
                    flightId={flight.id}
                    teamId={t.id}
                  />
                ) : null}
                {!isScramble && oppId && !oppName ? (
                  <p className="text-[10px] text-amber-700 mt-1">Opponent missing — re-pair</p>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
      {showAddTeam ? (
        <AddTeamModal
          tournamentId={tournamentId}
          flightId={flight.id}
          reSnapshotNet={!!tournamentInfo?.flightsLocked && (tournamentInfo?.handicapMode || 'none') !== 'none'}
          onClose={() => setShowAddTeam(false)}
        />
      ) : null}
    </div>
  )
}

// Per-player handicap inputs for Scramble teams, shown inside each team
// card on the Flights & Teams tab when format=Scramble + handicaps on.
// Local input state syncs from team.info.handicaps on every render so
// concurrent admin edits show through. Saves on blur (and on Enter) — no
// rapid-fire writes while typing. Empty string clears that player's
// handicap; scoring.js treats missing values as no contribution to team
// strokes.
function PlayerHandicapEditor({ tournamentId, flightId, team }) {
  const players = team.info?.players || []
  const stored = team.info?.handicaps || {}

  const initial = () => players.map((_, i) => {
    const v = stored[i]
    return v == null || !Number.isFinite(Number(v)) ? '' : String(v)
  })
  const [vals, setVals] = useState(initial)

  // Sync from props whenever the team.info.handicaps map changes (another
  // admin edits, or a different team is rendered into the same component
  // slot). Compare serialized to avoid noisy resets while the user types.
  const lastStoredKey = JSON.stringify(initial())
  useEffect(() => {
    setVals(initial())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastStoredKey, players.length])

  const commit = (next) => {
    setTeamPlayerHandicaps(tournamentId, flightId, team.id, next)
      .catch((e) => console.warn('[scramble] handicap save failed', e))
  }

  if (players.length === 0) {
    return <div className="text-xs text-gray-400 italic mt-1">No players on this team yet.</div>
  }

  return (
    <ul className="mt-1 space-y-1">
      {players.map((name, i) => (
        <li key={i} className="flex items-center justify-between gap-2 text-xs">
          <span className="truncate text-gray-700 font-medium">{name}</span>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">HCP</span>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              max="54"
              step="1"
              className="w-16 rounded-md border border-gray-300 px-2 py-0.5 text-xs text-right"
              value={vals[i] ?? ''}
              onChange={(e) => {
                const next = [...vals]
                next[i] = e.target.value
                setVals(next)
              }}
              onBlur={() => commit(vals)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.currentTarget.blur()
                }
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

// Inline qualifying-score badge for FlightCard team cells. Color cues:
//   • green when under course par (the seeding hook for Championship Flight)
//   • black at par
//   • gray when over par
//   • light-gray dash when no score has been submitted yet
function QualifyingDisplay({ score, coursePar }) {
  const n = Number(score)
  const has = Number.isFinite(n) && n > 0
  const par = Number(coursePar) || 72
  let valueClass = 'text-gray-400'
  if (has) {
    if (n < par) valueClass = 'text-green-deep'
    else if (n === par) valueClass = 'text-gray-900'
    else valueClass = 'text-gray-700'
  }
  return (
    <div className="mt-1.5 flex items-baseline gap-2">
      <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold shrink-0">Qualifying</span>
      <span className={`text-sm font-extrabold ${valueClass}`}>
        {has ? n : '—'}
      </span>
    </div>
  )
}

// Read-only tee time / starting-hole display for the FlightCard team cells.
// Single source of truth for hole assignment is the Tee Times tab — this
// component just renders whatever the wave assignments resolve to.
function TeeTimeReadOnly({ teeTimes, startType, flightId, teamId }) {
  const rows = lookupTeamTeeTimes(teeTimes, startType, flightId, teamId)
  const day1 = rows.find((r) => Number(r.day) === 1) || rows[0]
  const value = day1?.holeOrTime || ''
  const isShotgun = day1?.isShotgun !== false
  const label = value
    ? (isShotgun ? `Hole ${value}` : value)
    : '—'
  return (
    <div className="mt-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold shrink-0">Hole</span>
        <span className={`text-xs font-bold ${value ? 'text-green-deep' : 'text-gray-400'}`}>
          {label}
        </span>
      </div>
      <div className="text-[10px] text-gray-400 italic mt-0.5">Assigned in Tee Times tab</div>
    </div>
  )
}

function CreateFlightModal({ tournamentId, onClose, flightCount }) {
  const defaultName = flightCount === 0 ? 'Championship Flight' : `Flight ${flightCount}`
  const [name, setName] = useState(defaultName)
  const [format, setFormat] = useState('4-Ball Best Ball')
  const [playStyle, setPlayStyle] = useState('')
  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    const info = { name: name.trim(), format }
    if (playStyle) info.playStyle = playStyle
    await createFlight(tournamentId, info)
    onClose()
  }
  return (
    <Modal title="New Flight" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name"><input autoFocus className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Format">
          <select className="input" value={format} onChange={(e) => setFormat(e.target.value)}>
            <option>4-Ball Best Ball</option>
            <option>Stroke Play</option>
            <option>Scramble</option>
            <option>Shamble</option>
            <option>Alternate Shot</option>
            <option>Stableford</option>
            <option>Team Cup</option>
          </select>
        </Field>
        <Field label="Scoring style override">
          <select className="input" value={playStyle} onChange={(e) => setPlayStyle(e.target.value)}>
            <option value="">Inherit from tournament</option>
            <option value="match">Match Play</option>
            <option value="stroke">Stroke Play</option>
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary">Create</button>
        </div>
      </form>
    </Modal>
  )
}

function AddTeamModal({ tournamentId, flightId, reSnapshotNet = false, onClose }) {
  const [name, setName] = useState('')
  const [p1, setP1] = useState('')
  const [p2, setP2] = useState('')
  const [hcp, setHcp] = useState('')
  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    await createTeam(tournamentId, flightId, {
      name: name.trim(),
      players: [p1, p2].filter(Boolean),
      handicap: hcp === '' ? null : Number(hcp),
    })
    // Teams already finalized (flights locked) + handicaps in play → keep net
    // data current for the new team. Best-effort; never blocks the add.
    if (reSnapshotNet) { try { await snapshotNetHandicaps(tournamentId) } catch { /* ignore */ } }
    onClose()
  }
  return (
    <Modal title="Add Team" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Team name"><input autoFocus className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Player 1"><input className="input" value={p1} onChange={(e) => setP1(e.target.value)} /></Field>
        <Field label="Player 2"><input className="input" value={p2} onChange={(e) => setP2(e.target.value)} /></Field>
        <Field label="Team handicap">
          <input type="number" step="0.1" className="input" value={hcp} onChange={(e) => setHcp(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary">Add</button>
        </div>
      </form>
    </Modal>
  )
}

function AutoAssignModal({ tournamentId, flights, players, onClose }) {
  // Pair players into teams of 2 by handicap, then distribute teams across flights.
  const [pairsPerFlight] = useState(() => {
    const flightCount = Math.max(1, flights.length)
    const sorted = [...players].sort((a, b) => (a.handicap ?? 99) - (b.handicap ?? 99))
    // Pair: 1st w/ last, 2nd w/ 2nd-to-last, etc. — gives balanced team handicaps.
    const teams = []
    let i = 0, j = sorted.length - 1
    while (i < j) {
      const a = sorted[i++], b = sorted[j--]
      teams.push({
        name: `${a.name.split(' ')[0]} & ${b.name.split(' ')[0]}`,
        players: [a.name, b.name],
        handicap: ((a.handicap ?? 0) + (b.handicap ?? 0)) / 2,
      })
    }
    if (i === j) {
      teams.push({ name: sorted[i].name, players: [sorted[i].name], handicap: sorted[i].handicap ?? 0 })
    }
    const buckets = autoAssignFlights(teams, flightCount)
    return buckets
  })

  const apply = async () => {
    for (let i = 0; i < flights.length && i < pairsPerFlight.length; i++) {
      for (const team of pairsPerFlight[i]) {
        await createTeam(tournamentId, flights[i].id, team)
      }
    }
    onClose()
  }

  return (
    <Modal title="Auto-assign teams" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-3">
        Pairs players by handicap (best with worst) and distributes teams across your existing flights.
      </p>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {flights.map((f, i) => (
          <div key={f.id} className="rounded-lg border border-gray-200 p-2">
            <div className="text-xs font-bold text-gray-500 uppercase">{f.info?.name}</div>
            <ul className="text-sm mt-1">
              {(pairsPerFlight[i] || []).map((t, k) => (
                <li key={k}>{t.name} <span className="text-gray-400 text-xs">HCP {t.handicap.toFixed(1)}</span></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-3">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={apply}>Apply</button>
      </div>
    </Modal>
  )
}

// ---------------------- Live Scores ----------------------

function LiveScoresTab({ tournamentId, flights, pars }) {
  if (flights.length === 0) return <div className="card text-gray-500 text-center py-10">No flights yet.</div>
  return (
    <div className="space-y-6">
      {flights.map((f) => (
        <div key={f.id} className="card">
          <h3 className="font-bold text-lg">{f.info?.name}</h3>
          <p className="text-xs text-gray-500 mb-3">Tap a cell to edit. Updates save instantly.</p>
          <FlightScoreGrid tournamentId={tournamentId} flight={f} pars={pars} />
        </div>
      ))}
    </div>
  )
}

function FlightScoreGrid({ tournamentId, flight, pars }) {
  const teamsRaw = entriesAsList(flight.teams)
  // Reorder so opponent pairings sit adjacent. Each pair is tagged with
  // pairingIdx + side ('A'/'B'); solo teams (no opponentId or unpaired)
  // get their own pairingIdx so they read as a single-row group. The grid
  // uses these tags to alternate row tint per pairing and draw a thicker
  // divider above the first row of each pair.
  const teams = (() => {
    const byId = Object.fromEntries(teamsRaw.map((t) => [t.id, t]))
    const seen = new Set()
    const out = []
    let pairingIdx = 0
    for (const t of teamsRaw) {
      if (seen.has(t.id)) continue
      const oppId = t.info?.opponentId
      if (oppId && byId[oppId] && oppId !== t.id) {
        seen.add(t.id); seen.add(oppId)
        out.push({ ...t, pairingIdx, sideOfPair: 'A' })
        out.push({ ...byId[oppId], pairingIdx, sideOfPair: 'B' })
      } else {
        seen.add(t.id)
        out.push({ ...t, pairingIdx, sideOfPair: 'solo' })
      }
      pairingIdx++
    }
    return out
  })()
  const lastUpdate = (scores) => {
    if (!scores) return 0
    let max = 0
    for (let h = 1; h <= 18; h++) {
      const s = scores[h]
      if (s && s.updatedAt && s.updatedAt > max) max = s.updatedAt
    }
    return max
  }
  const stale = (ts) => {
    if (!ts) return true
    return Date.now() - ts > 30 * 60 * 1000 // >30 min = behind pace
  }

  if (teams.length === 0) return <p className="text-gray-500 text-sm">No teams.</p>
  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse">
        <thead>
          <tr>
            <th className="sticky left-0 bg-white p-2 text-left">Team</th>
            <th className="p-2">Pace</th>
            {Array.from({ length: 18 }, (_, i) => (
              <th key={i} className="p-1 text-center text-gray-500 font-medium">
                {i + 1}<br /><span className="text-[10px] text-gray-400">P{pars[i]}</span>
              </th>
            ))}
            <th className="p-2 text-right">Thru</th>
            <th className="p-2 text-right">Gross</th>
            <th className="p-2 text-right">vs Par</th>
            <th className="p-2"></th>
          </tr>
        </thead>
        <tbody>
          {teams.map((t) => {
            const ts = lastUpdate(t.scores)
            const locked = !!t.info?.locked
            // Subtle alternating pairing tint + thicker top border on the
            // first row of each pair. Side B has no top border so visually
            // it joins side A as one block.
            const isOddPair = t.pairingIdx % 2 === 1
            const isPairTop = t.sideOfPair === 'A' || t.sideOfPair === 'solo'
            const tintBg = isOddPair ? '#F3F6F2' : '#FFFFFF'
            const stickyBg = isOddPair ? '#F3F6F2' : '#FFFFFF'
            const rowBorder = isPairTop ? '2px solid var(--ge-primary)40' : '1px dashed #E5E7EB'
            return (
              <tr key={t.id} style={{ backgroundColor: tintBg, borderTop: rowBorder }}>
                <td className="sticky left-0 p-2 font-semibold" style={{ backgroundColor: stickyBg }}>
                  {t.sideOfPair === 'A' ? <span className="text-[9px] text-gray-500 mr-1">A</span> : null}
                  {t.sideOfPair === 'B' ? <span className="text-[9px] text-gray-500 mr-1">B</span> : null}
                  {lastNames(t.info?.name)}
                  {locked ? <span className="ml-2 pill-gray text-[9px]">Locked</span> : null}
                </td>
                <td className="p-2 text-center">
                  {ts ? (
                    <span className={stale(ts) ? 'pill-amber text-[9px]' : 'pill-green text-[9px]'}>
                      {stale(ts) ? 'Slow' : 'OK'}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                {Array.from({ length: 18 }, (_, i) => {
                  const h = i + 1
                  const s = t.scores?.[h]
                  return (
                    <td key={h} className="p-0.5 text-center">
                      <ScoreEditor
                        tournamentId={tournamentId}
                        flightId={flight.id}
                        teamId={t.id}
                        hole={h}
                        score={s}
                        disabled={locked}
                      />
                    </td>
                  )
                })}
                <td className="p-2 text-right font-semibold">{teamThru(t.scores)}</td>
                <td className="p-2 text-right font-semibold">{teamGross(t.scores) || '—'}</td>
                <td className="p-2 text-right font-semibold">{fmtVsPar(teamVsPar(t.scores, pars))}</td>
                <td className="p-2 text-right">
                  <button
                    className="text-xs text-gray-600 hover:text-green-deep font-semibold"
                    onClick={() => lockTeamScores(tournamentId, flight.id, t.id, !locked)}>
                    {locked ? 'Unlock' : 'Lock'}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ScoreEditor({ tournamentId, flightId, teamId, hole, score, disabled }) {
  const [editing, setEditing] = useState(false)
  if (editing) {
    return (
      <ScoreInline
        initial={score}
        onCancel={() => setEditing(false)}
        onSave={async (p1, p2) => {
          await setHoleScore(tournamentId, flightId, teamId, hole, p1, p2)
          setEditing(false)
        }}
      />
    )
  }
  return (
    <button
      className={`w-12 h-9 rounded-md text-sm font-bold ${
        score?.bestBall ? 'bg-green-mint text-green-deep' : 'bg-gray-50 text-gray-400'
      } ${disabled ? 'opacity-50' : 'hover:bg-green-mint/70'}`}
      onClick={() => !disabled && setEditing(true)}>
      {score?.bestBall ?? '—'}
    </button>
  )
}

function ScoreInline({ initial, onSave, onCancel }) {
  const [p1, setP1] = useState(initial?.p1score ?? '')
  const [p2, setP2] = useState(initial?.p2score ?? '')
  return (
    <div className="flex flex-col gap-1 p-1 bg-yellow-50 rounded">
      <input
        autoFocus
        type="number"
        className="w-12 text-center text-xs rounded border border-gray-300 px-1 py-0.5"
        value={p1}
        onChange={(e) => setP1(e.target.value)}
        placeholder="P1"
      />
      <input
        type="number"
        className="w-12 text-center text-xs rounded border border-gray-300 px-1 py-0.5"
        value={p2}
        onChange={(e) => setP2(e.target.value)}
        placeholder="P2"
      />
      <div className="flex gap-1">
        <button
          className="text-[10px] bg-green-fairway text-white rounded px-1.5 py-0.5"
          onClick={() => onSave(Number(p1) || null, Number(p2) || null)}>✓</button>
        <button className="text-[10px] bg-gray-300 rounded px-1.5 py-0.5" onClick={onCancel}>×</button>
      </div>
    </div>
  )
}

// ---------------------- Contests ----------------------

function ContestsTab({ tournamentId, contests }) {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <KPListCard tournamentId={tournamentId} contests={contests} />
      <ContestCard
        title="Long Drive"
        contest={contests.longdrive}
        onSave={(v) => setContest(tournamentId, 'longdrive', v)}
      />
      <ContestCard
        title="Longest Putt"
        contest={contests.longputt}
        distancePlaceholder={`e.g. 42'`}
        onSave={(v) => setContest(tournamentId, 'longputt', v)}
      />
      <ContestCard
        title="Hole in One"
        contest={contests.hio}
        prizeMode
        prizePlaceholder="e.g. New driver from the pro shop"
        onSave={(v) => setContest(tournamentId, 'hio', v)}
      />
      <ContestCard
        title="Skins (notes)"
        contest={contests.skins}
        skinsMode
        onSave={(v) => setContest(tournamentId, 'skins', v)}
      />
    </div>
  )
}

// Multi-KP card. Stored at contests.kps as an array. Falls back to legacy
// contests.kp (single) on first read; first save normalizes to the array.
function KPListCard({ tournamentId, contests }) {
  const seed = (() => {
    const raw = contests?.kps
    if (Array.isArray(raw)) return raw.filter(Boolean)
    if (raw && typeof raw === 'object') {
      return Object.keys(raw).sort().map((k) => raw[k]).filter(Boolean)
    }
    if (contests?.kp?.winner) return [contests.kp]
    return []
  })()

  const [rows, setRows] = useState(
    seed.length > 0 ? seed : [{ hole: '', winner: '', distance: '' }],
  )
  const [savedAt, setSavedAt] = useState(0)

  const updateRow = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const addRow = () => setRows((rs) => [...rs, { hole: '', winner: '', distance: '' }])
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i))

  const save = async () => {
    const cleaned = rows
      .map((r) => ({
        hole: Number(r.hole) || null,
        winner: (r.winner || '').trim(),
        distance: (r.distance || '').trim(),
      }))
      .filter((r) => r.hole || r.winner)
    await setContest(tournamentId, 'kps', cleaned)
    // Legacy single-KP key gets in the way of the new array — clear it once.
    if (contests?.kp) await setContest(tournamentId, 'kp', null)
    setSavedAt(Date.now())
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center">
        <h3 className="font-bold">Closest to the Pin (KP)</h3>
        <button className="btn-ghost text-xs" onClick={addRow}>+ Add KP Hole</button>
      </div>
      <div className="space-y-3 mt-3">
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500">No KP holes yet. Click "+ Add KP Hole".</p>
        ) : (
          rows.map((r, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-bold">KP #{i + 1}</span>
                <button
                  className="text-red-600 text-xs font-semibold"
                  onClick={() => removeRow(i)}>Remove</button>
              </div>
              <Field label="Hole #">
                <input
                  type="number" min="1" max="18" className="input"
                  value={r.hole || ''}
                  onChange={(e) => updateRow(i, { hole: e.target.value })}
                />
              </Field>
              <Field label="Winner">
                <input
                  className="input"
                  value={r.winner || ''}
                  onChange={(e) => updateRow(i, { winner: e.target.value })}
                />
              </Field>
              <Field label="Distance">
                <input
                  className="input"
                  value={r.distance || ''}
                  onChange={(e) => updateRow(i, { distance: e.target.value })}
                  placeholder={`e.g. 4ft 8in`}
                />
              </Field>
            </div>
          ))
        )}
        <button className="btn-primary w-full" onClick={save}>
          Save All KPs
        </button>
        {savedAt ? (
          <p className="text-xs text-green-deep text-center">Saved · {new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p>
        ) : null}
      </div>
    </div>
  )
}

function ContestCard({ title, contest, onSave, skinsMode, prizeMode, distancePlaceholder, prizePlaceholder }) {
  const [hole, setHole] = useState(contest?.hole || '')
  const [winner, setWinner] = useState(contest?.winner || '')
  const [distance, setDistance] = useState(contest?.distance || '')
  const [prize, setPrize] = useState(contest?.prize || '')
  const [notes, setNotes] = useState(contest?.notes || '')
  // prizeMode replaces the Distance field with a Prize field — used for
  // Hole-in-One where the admin pre-announces a prize on the chosen hole
  // and only fills the winner if/when someone aces it.
  return (
    <div className="card">
      <h3 className="font-bold">{title}</h3>
      <div className="space-y-2 mt-3">
        {skinsMode ? (
          <Field label="Notes">
            <textarea className="input min-h-[120px]" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Skins won on holes 4, 7, 12…" />
          </Field>
        ) : (
          <>
            <Field label="Hole #">
              <input type="number" min="1" max="18" className="input" value={hole} onChange={(e) => setHole(e.target.value)} />
            </Field>
            <Field label={prizeMode ? 'Prize' : 'Winner'}>
              {prizeMode ? (
                <input
                  className="input"
                  value={prize}
                  onChange={(e) => setPrize(e.target.value)}
                  placeholder={prizePlaceholder || 'e.g. New driver from the pro shop'}
                />
              ) : (
                <input className="input" value={winner} onChange={(e) => setWinner(e.target.value)} />
              )}
            </Field>
            {prizeMode ? (
              <Field label="Winner (if any)">
                <input className="input" value={winner} onChange={(e) => setWinner(e.target.value)} placeholder="Leave blank until aced" />
              </Field>
            ) : (
              <Field label="Distance">
                <input
                  className="input"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  placeholder={distancePlaceholder || `e.g. 6' 3"`}
                />
              </Field>
            )}
          </>
        )}
        <button
          className="btn-primary w-full"
          onClick={() => onSave(
            skinsMode
              ? { notes }
              : prizeMode
                ? { hole: Number(hole) || null, prize, winner }
                : { hole: Number(hole) || null, winner, distance },
          )}>
          Save
        </button>
      </div>
    </div>
  )
}

// ---------------------- Results / Export ----------------------

function ResultsTab({ tournamentId, tournament, info, flights, pars }) {
  const [useNet, setUseNet] = useState(false)

  const ranked = useMemo(
    () => flights.map((f) => ({ flight: f, rows: rankTeams(entriesAsList(f.teams), useNet, pars) })),
    [flights, useNet, pars],
  )

  // Calcutta payouts: combine all flights into one tournament-wide ranking,
  // walk the configured payout structure, and pair each place with the team
  // that finished there + its Calcutta owner (from calcutta.bids[teamId]).
  const calcuttaSettings = tournament?.calcutta?.settings || {}
  const calcuttaBids = tournament?.calcutta?.bids || {}
  const calcuttaActive = !!calcuttaSettings.enabled
  const calcuttaPayouts = useMemo(() => {
    if (!calcuttaActive) return null
    let totalPot = 0
    for (const b of Object.values(calcuttaBids)) {
      totalPot += Number(b.bidAmount) || 0
      if (calcuttaSettings.buyBackEnabled && b.buyBack) totalPot += Number(b.buyBackCost) || 0
    }
    const overall = []
    for (const { flight, rows } of ranked) {
      for (const r of rows) overall.push({ ...r, flightName: flight.info?.name })
    }
    overall.sort((a, b) => {
      const av = useNet ? a.net : a.gross
      const bv = useNet ? b.net : b.gross
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      return av - bv
    })
    const structure = Array.isArray(calcuttaSettings.payoutStructure) ? calcuttaSettings.payoutStructure : []
    const places = structure.map((row, i) => {
      const team = overall[i]
      const owner = team ? calcuttaBids[team.id] : null
      return {
        place: row.place,
        pct: Number(row.pct) || 0,
        amount: Math.round(totalPot * (Number(row.pct) || 0) / 100),
        team,
        ownerName: owner?.buyerName || null,
      }
    })
    return { totalPot, places }
  }, [calcuttaActive, calcuttaBids, calcuttaSettings, ranked, useNet])

  const exportCsv = () => {
    const lines = []
    lines.push(`"${info?.name || 'Tournament'}","${info?.date || ''}","${info?.course || ''}"`)
    lines.push('')
    for (const { flight, rows } of ranked) {
      lines.push(`"Flight: ${flight.info?.name || ''}"`)
      lines.push('Rank,Team,Players,Thru,Gross,Net,vs Par')
      rows.forEach((r, i) => {
        lines.push([
          i + 1,
          `"${r.info?.name || ''}"`,
          `"${(r.info?.players || []).join('; ')}"`,
          r.thru,
          r.gross,
          r.net,
          fmtVsPar(r.vsPar),
        ].join(','))
      })
      lines.push('')
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(info?.name || 'tournament').replace(/\s+/g, '_')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap justify-between items-center gap-3 no-print">
        <h2 className="text-lg font-bold">Final Results</h2>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={useNet} onChange={(e) => setUseNet(e.target.checked)} /> Show net
          </label>
          <button className="btn-secondary" onClick={() => window.print()}>🖨 Print</button>
          <button className="btn-primary" onClick={exportCsv}>Download CSV</button>
        </div>
      </div>

      <div className="text-center print:block hidden">
        <h1 className="text-3xl font-extrabold">{info?.name}</h1>
        <p className="text-gray-600">{info?.course} · {info?.date}</p>
      </div>

      {ranked.map(({ flight, rows }) => (
        <div key={flight.id} className="card">
          <h3 className="font-bold text-lg mb-2">{flight.info?.name}</h3>
          {rows.length === 0 ? (
            <p className="text-gray-500 text-sm">No teams.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 w-10">#</th>
                  <th className="text-left py-2">Team</th>
                  <th className="text-left py-2">Players</th>
                  <th className="text-right py-2">Thru</th>
                  <th className="text-right py-2">{useNet ? 'Net' : 'Gross'}</th>
                  <th className="text-right py-2">vs Par</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className={`border-b last:border-0 ${i === 0 ? 'bg-green-mint/40' : ''}`}>
                    <td className="py-2 font-bold">{i + 1}</td>
                    <td className="py-2 font-semibold">{lastNames(r.info?.name)}</td>
                    <td className="py-2 text-gray-600">{(r.info?.players || []).join(' · ')}</td>
                    <td className="py-2 text-right">{r.thru}</td>
                    <td className="py-2 text-right font-bold">{useNet ? r.net : r.gross}</td>
                    <td className="py-2 text-right">{fmtVsPar(r.vsPar)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}

      {calcuttaPayouts ? (
        <div className="card">
          <div className="flex justify-between items-end flex-wrap gap-2">
            <h3 className="font-bold text-lg">💰 Calcutta Payouts</h3>
            <div className="text-sm text-gray-600">
              Total Pot: <b className="text-green-deep">${calcuttaPayouts.totalPot.toLocaleString()}</b>
            </div>
          </div>
          <table className="w-full text-sm mt-2">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 w-10">#</th>
                <th className="text-left py-2">Team</th>
                <th className="text-left py-2">Owner</th>
                <th className="text-right py-2">Payout</th>
              </tr>
            </thead>
            <tbody>
              {calcuttaPayouts.places.map((p) => (
                <tr key={p.place} className="border-b last:border-0">
                  <td className="py-2 font-bold">{p.place}</td>
                  <td className="py-2 font-semibold">
                    {p.team ? (
                      <>
                        {lastNames(p.team.info?.name)}
                        <div className="text-[11px] text-gray-500 font-normal">{p.team.flightName}</div>
                      </>
                    ) : <span className="text-gray-400 italic">—</span>}
                  </td>
                  <td className="py-2">
                    {p.ownerName || <span className="text-gray-400 italic">no owner</span>}
                  </td>
                  <td className="py-2 text-right font-bold text-green-deep">${p.amount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}

// ---------------- Handicap & Courses quick-picks ----------------
// Live chip rows rendered above the existing CourseSearchPicker (in
// EditInfoModal) and above the existing Name input (in AddPlayerModal).
// Purely additive — clicking a chip pre-fills the parent's state via
// onPick. The pickers and form fields themselves are unchanged.

function SavedCoursesQuickPick({ onPick }) {
  const [courses, setCourses] = useState({})
  useEffect(() => watchClubCourses(setCourses), [])
  const list = Object.entries(courses)
    .map(([id, v]) => ({ id, ...(v || {}) }))
    .sort((a, b) => (a.courseName || '').localeCompare(b.courseName || ''))
  if (list.length === 0) return null
  return (
    <div className="mb-2">
      <p className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1.5">
        Quick-pick from Handicap & Courses
      </p>
      <div className="flex flex-wrap gap-1.5">
        {list.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onPick(quickPickCoursePayload(c))}
            className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300">
            {c.courseName}
          </button>
        ))}
      </div>
    </div>
  )
}

// See AdminApp.jsx — saved courses carry a teeBoxes array now; legacy
// single-tee records get synthesized into a 1-element array. When
// multiple tees exist the director picks the right one for THIS
// tournament via the CourseSearchPicker's dropdown.
function quickPickCoursePayload(c) {
  const teeBoxes = Array.isArray(c?.teeBoxes) && c.teeBoxes.length > 0
    ? c.teeBoxes.map((t) => ({
        name: t.name || '',
        rating: Number(t.rating) || 0,
        slope: Number(t.slope) || 0,
        par: Number(t.par) || Number(c.coursePar) || 72,
      }))
    : (c?.teeName && Number(c?.slopeRating) > 0 && Number(c?.courseRating) > 0
        ? [{
            name: c.teeName,
            slope: Number(c.slopeRating),
            rating: Number(c.courseRating),
            par: Number(c.coursePar) || 72,
          }]
        : [])
  if (teeBoxes.length === 1) {
    const t = teeBoxes[0]
    return {
      courseName: c.courseName || '',
      courseId: c.courseId || null,
      coursePar: Number(c.coursePar) || Number(t.par) || null,
      slopeRating: t.slope,
      courseRating: t.rating,
      teeName: t.name,
      teeBoxes,
    }
  }
  return {
    courseName: c.courseName || '',
    courseId: c.courseId || null,
    coursePar: Number(c.coursePar) || null,
    slopeRating: null,
    courseRating: null,
    teeName: null,
    teeBoxes,
  }
}

function SavedPlayersQuickPick({ onPick }) {
  const [players, setPlayers] = useState({})
  const [query, setQuery] = useState('')
  useEffect(() => watchClubPlayers(setPlayers), [])
  const list = Object.entries(players).map(([id, v]) => ({ id, ...(v || {}) }))
  const q = query.trim().toLowerCase()
  const filtered = (q
    ? list.filter((p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.ghin || '').includes(q),
      )
    : list
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  if (list.length === 0) return null
  return (
    <div className="mb-2">
      <p className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1.5">
        Pick from Roster
      </p>
      <input
        className="input text-sm mb-1.5"
        placeholder="Filter roster by name, email, or handicap #…"
        value={query}
        onChange={(e) => setQuery(e.target.value)} />
      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
        {filtered.slice(0, 30).map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(p)}
            className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300">
            {p.name}
          </button>
        ))}
        {filtered.length === 0 ? (
          <span className="text-xs text-gray-500">No matches.</span>
        ) : null}
      </div>
    </div>
  )
}

// Per-row status under the GHIN field in the admin-side Add Player
// modal. Light-theme styling matched to the modal it lives in.
function AdminGhinStatusLine({ status, index }) {
  if (status === 'loading') {
    return <p className="text-xs text-gray-600" role="status">Looking up handicap…</p>
  }
  if (status === 'ok') {
    return (
      <p className="text-xs font-bold text-green-700" role="status">
        ✓ Handicap Index pulled from handicap lookup: <span className="font-extrabold">{index}</span>
      </p>
    )
  }
  if (status === 'notfound') {
    return (
      <p className="text-xs text-red-600" role="status">
        Handicap number not found — enter the Handicap Index manually below.
      </p>
    )
  }
  if (status === 'auth' || status === 'network') {
    return (
      <p className="text-xs text-red-600" role="status">
        Couldn't reach the handicap lookup service — enter the Handicap Index manually below.
      </p>
    )
  }
  return null
}


// Modified Stableford points-table editor. Writes info.modStablefordTable, which
// the scoring + leaderboard already honor end-to-end. Reuses the config-card chrome.
function ModStablefordEditor({ tournamentId, info }) {
  const saved = info?.modStablefordTable || DEFAULT_MOD_STABLEFORD
  const [tbl, setTbl] = useState({ ...DEFAULT_MOD_STABLEFORD, ...saved })
  const [ok, setOk] = useState(false)
  const ROWS = [
    ['albatross', 'Albatross+'], ['eagle', 'Eagle'], ['birdie', 'Birdie'],
    ['par', 'Par'], ['bogey', 'Bogey'], ['double', 'Double+'],
  ]
  const save = async () => {
    const clean = {}
    for (const [k] of ROWS) clean[k] = Number(tbl[k]) || 0
    await updateTournamentInfo(tournamentId, { modStablefordTable: clean })
    setOk(true)
  }
  return (
    <div className="card">
      <h3 className="font-bold text-lg mb-3">Modified Stableford Points</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {ROWS.map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
            <input
              type="number"
              className="input mt-1"
              value={tbl[k]}
              onChange={(e) => { setTbl((t) => ({ ...t, [k]: e.target.value })); setOk(false) }}
            />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button className="btn-primary text-sm" onClick={save}>Save</button>
        {ok ? <span className="text-sm font-bold text-green-700">Saved ✓</span> : null}
      </div>
    </div>
  )
}
